import { dash, formatFleetValue, isUnitBillable } from '../fleetLiveHelpers'

const ACTIVE_COLOR = '#46d39a'
const IDLE_COLOR = 'rgba(68, 68, 68, 0.24)'
const WARNING_COLOR = '#f0a93b'
const ERROR_COLOR = '#f0584e'

export const PUMP_STATUS_ERROR = 'error'
export const PUMP_STATUS_WARNING = 'warning'
export const PUMP_STATUS_RUNNING = 'running'
export const PUMP_STATUS_IDLE = 'idle'

export const PUMP_STATUS_COLORS = {
  [PUMP_STATUS_ERROR]: ERROR_COLOR,
  [PUMP_STATUS_WARNING]: WARNING_COLOR,
  [PUMP_STATUS_RUNNING]: ACTIVE_COLOR,
  [PUMP_STATUS_IDLE]: IDLE_COLOR,
}

const EVENT_TYPE_NAMES = ['Event', 'Info', 'Warning', 'Error']

/** EventType comes as a string ("Warning"); numeric values are accepted too. */
export const getEventTypeName = (type) => {
  if (typeof type === 'number') return EVENT_TYPE_NAMES[type] || String(type)
  return type == null ? '' : String(type)
}

/** Warning/Error events of the pump that are still active (sent by the server, errors first). */
export const getActiveEvents = (unit) => (
  Array.isArray(unit?.activeEvents) ? unit.activeEvents : []
)

const FIELD_DECIMALS = {
  Gear: 0,
  RPM: 0,
  DischargePressure: 0,
  'Discharge Pressure': 0,
  DischargeRate: 1,
  'Discharge Rate': 1,
  Fuel: 1,
  Diesel: 1,
  GasSub: 1,
  'Gas Sub': 1,
  TransTemp: 0,
  'Trans Temp': 0,
  CoolantTemp: 0,
  'Coolant Temp': 0,
  Suction: 1,
  RodLoad: 1,
  'Rod Load': 1,
}

const normalizeUnit = (unit) => {
  if (unit == null || unit === '' || unit === '-') return ''
  return String(unit)
}

const decimalsFor = (fieldName) => {
  if (FIELD_DECIMALS[fieldName] != null) return FIELD_DECIMALS[fieldName]
  const compact = String(fieldName || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
  const match = Object.entries(FIELD_DECIMALS).find(
    ([key]) => key.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() === compact
  )
  return match ? match[1] : 1
}

export const formatFieldDisplay = (value, unit, decimals = 1) => {
  const formatted = formatFleetValue(value, decimals)
  if (formatted === dash) return dash
  const suffix = normalizeUnit(unit)
  return suffix ? `${formatted} ${suffix}` : formatted
}

export const getPumpDashboardFields = (unit) => {
  const fields = Array.isArray(unit?.fields) ? unit.fields : []
  return fields
    .filter((field) => field && field.isDashboardField && field.fieldName)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
}

/** First 2 dashboard fields (by order) go into the main grey block; the rest are metrics. */
export const splitDashboardMainAndMetrics = (dashboardFields = []) => {
  const sorted = [...dashboardFields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  return {
    mainFields: sorted.slice(0, 2),
    metricFields: sorted.slice(2),
  }
}

export const isPumpActive = (unit) => {
  const dashboard = getPumpDashboardFields(unit)
  if (dashboard.length) {
    return dashboard.some((field) => Number(field.value) > 0)
  }

  const data = unit?.pumpData
  if (!data) return false
  return (
    Number(data.rpm) > 0
    || Number(data.gear) > 0
    || Number(data.dischargeRate) > 0
    || Number(data.dischargePressure) > 0
  )
}

/** Running = Engine RPM > 0. Without an Engine RPM value, any dashboard field > 0 counts as before. */
export const isPumpRunning = (unit) => {
  if (unit?.engineRpm != null && !Number.isNaN(Number(unit.engineRpm))) {
    return Number(unit.engineRpm) > 0
  }
  return isPumpActive(unit)
}

/** A stopped pump (Engine RPM = 0) is idle whatever its events; a running one shows Error over Warning over running. */
export const getPumpStatus = (unit) => {
  if (!isUnitBillable(unit) || !isPumpRunning(unit)) return PUMP_STATUS_IDLE
  const types = getActiveEvents(unit).map((event) => getEventTypeName(event?.type).toLowerCase())
  if (types.includes('error')) return PUMP_STATUS_ERROR
  if (types.includes('warning')) return PUMP_STATUS_WARNING
  return PUMP_STATUS_RUNNING
}

const DISCHARGE_RATE_KEY = 'dischargerate'

/** Average Discharge Rate of the running pumps (Engine RPM > 0). */
export const getDownholeFlowReading = (pumps = []) => {
  const rates = pumps
    .filter((unit) => isUnitBillable(unit) && isPumpRunning(unit))
    .map((unit) => getPumpDashboardFields(unit).find(
      (field) => String(field.fieldName).replace(/[^a-zA-Z0-9]/g, '').toLowerCase() === DISCHARGE_RATE_KEY
    ))
    .filter((field) => field && field.value != null && !Number.isNaN(Number(field.value)))

  const unit = rates.find((field) => normalizeUnit(field.unit))?.unit
  const average = rates.length
    ? rates.reduce((sum, field) => sum + Number(field.value), 0) / rates.length
    : null
  return {
    value: formatFleetValue(average, decimalsFor('Discharge Rate')),
    unit: normalizeUnit(unit),
  }
}

export const mapPumpUnitToCard = (unit) => {
  const billable = isUnitBillable(unit)
  const active = billable && isPumpRunning(unit)
  const status = getPumpStatus(unit)
  const data = unit?.pumpData || {}
  const dashboard = getPumpDashboardFields(unit)
  const byName = dashboard.reduce((acc, field) => {
    acc[field.fieldName] = field
    return acc
  }, {})

  const { mainFields, metricFields } = splitDashboardMainAndMetrics(dashboard)

  const mainMetrics = (dashboard.length
    ? mainFields
    : [
      { fieldName: 'Gear', value: data.gear, unit: '' },
      { fieldName: 'RPM', value: data.rpm, unit: '' },
    ]
  ).map((field) => ({
    key: field.fieldName,
    label: field.fieldName || '',
    display: formatFieldDisplay(
      field.value,
      field.unit,
      decimalsFor(field.fieldName)
    ),
  }))

  const metrics = dashboard.length
    ? metricFields.map((field) => ({
      key: field.fieldName,
      label: field.fieldName || '',
      display: formatFieldDisplay(
        field.value,
        field.unit,
        decimalsFor(field.fieldName)
      ),
    }))
    : [
      { key: 'Fuel', label: 'Fuel', display: formatFieldDisplay(data.fuel, 'gal', 1) },
      { key: 'GasSub', label: 'GasSub', display: formatFieldDisplay(data.gasSub, '%', 1) },
      { key: 'TransTemp', label: 'TransTemp', display: formatFieldDisplay(data.transTemp, '°F', 0) },
      { key: 'CoolantTemp', label: 'CoolantTemp', display: formatFieldDisplay(data.coolantTemp, '°F', 0) },
      { key: 'Suction', label: 'Suction', display: formatFieldDisplay(data.suction, 'psi', 1) },
      { key: 'RodLoad', label: 'RodLoad', display: formatFieldDisplay(data.rodLoad, '%', 1) },
      {
        key: 'DischargePressure',
        label: 'DischargePressure',
        display: formatFieldDisplay(data.dischargePressure, 'psi', 0),
      },
      {
        key: 'DischargeRate',
        label: 'DischargeRate',
        display: formatFieldDisplay(data.dischargeRate, 'bbl/min', 1),
      },
    ]

  // Status grid: first 2 dashboard fields only (no hardcoded field names / units)
  const statusMetrics = mainFields.map((field) => ({
    key: field.fieldName,
    display: formatFieldDisplay(
      field.value,
      field.unit,
      field.decimals != null ? field.decimals : decimalsFor(field.fieldName)
    ),
  }))

  const transName = unit?.unitUId || ''

  return {
    id: unit.unitUId,
    name: unit.unitName || unit.unitUId,
    unit,
    billable,
    active,
    status,
    // Idle pumps open no detail panel; the status tooltip (Engine RPM) is shown for every billable pump
    selectable: active,
    pulse: active,
    statusColor: PUMP_STATUS_COLORS[status],
    statusText: active ? 'Active' : 'Idle',
    trans: transName || dash,
    mainMetrics,
    statusPrimary: statusMetrics[0]?.display || dash,
    statusSecondary: statusMetrics[1]?.display || dash,
    metrics,
    gear: formatFleetValue(byName.Gear?.value ?? data.gear, 0),
    gearCell: (byName.Gear?.value ?? data.gear) == null
      ? dash
      : String(byName.Gear?.value ?? data.gear),
    rpm: formatFleetValue(byName.RPM?.value ?? data.rpm, 0),
    fuel: formatFleetValue(byName.Fuel?.value ?? data.fuel, 1),
    gasSub: formatFleetValue(byName.GasSub?.value ?? data.gasSub, 1),
    transT: formatFleetValue(byName.TransTemp?.value ?? data.transTemp, 0),
    cool: formatFleetValue(byName.CoolantTemp?.value ?? data.coolantTemp, 0),
    suct: formatFleetValue(byName.Suction?.value ?? data.suction, 1),
    rod: formatFleetValue(byName.RodLoad?.value ?? data.rodLoad, 1),
  }
}
